import React, { useState } from "react";

import { Modal, Row } from "antd";
import { FormattedMessage } from "react-intl";
import { Colors } from "../../../../../shared/theme";
import { textStyles } from "../../../../../shared/theme/fonts-ppv";
import RBHTable2 from "../../../../../shared/components/rbh-table-2";
import { RBHButton, RBHText, RBHTextInput } from "../../../../../shared/components";

import './styles.css'
import RbhText from "../../../../../shared/components/rbh-text";
import { SearchOutlined } from "@ant-design/icons";
import { DEBOUNCE_TIMEOUT } from "../../../../../shared/helpers/constant";
import metrics from "../../../../../shared/theme/metrics";

interface SelectModifierItemModalProps {
    isItemModalOpen: boolean;
    setIsItemModalOpen: (isOpen: boolean) => void;
    tableData: any[];
    tempSelectedIds: number[];
    setTempSelectedIds: (ids: number[]) => void;
    handleConfirmItemModal: () => void;
    maxItemPerGroup: number;
}

const SelectModifierItemModal = ({
    isItemModalOpen,
    setIsItemModalOpen,
    tableData,
    tempSelectedIds,
    setTempSelectedIds,
    handleConfirmItemModal,
    maxItemPerGroup,
}: SelectModifierItemModalProps) => {
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, DEBOUNCE_TIMEOUT);

        return () => {
            clearTimeout(handler);
        };
    }, [searchText]);

    const filteredTableData = React.useMemo(() => {
        if (!debouncedSearchText.trim()) return tableData;
        const keyword = debouncedSearchText.toLowerCase();
        return tableData.filter((record) =>
            record.menuModifierItemName?.toLowerCase().includes(keyword) ||
            record.menuModifierItemNameEN?.toLowerCase().includes(keyword)
        );
    }, [debouncedSearchText, tableData, tempSelectedIds]);

    const isTempMaxReached = maxItemPerGroup > 0 && tempSelectedIds.length >= maxItemPerGroup;
    
    return (
         <Modal
            wrapClassName="select-modifier-item-modal"
            visible={isItemModalOpen}
            footer={null}
            bodyStyle={{ textAlign: 'start' }}
            closable={false}
        >
            <Row align={'middle'} justify='start' style={{ marginBottom: 24 }}>
                <RBHText
                    style={{ ...textStyles.h3, color: Colors.grey800 }}
                    text={'main.menu.modifiergroup.form.modal.items.title'} />
            </Row>
             <Row align={'middle'} justify='space-between' style={{ marginBottom: 16 }}>
                <RbhText style={{ ...textStyles.body2, color: Colors.secondary }} text="main.menu.modifiergroup.form.modal.items.selected" values={{ selected: tempSelectedIds.length, max: maxItemPerGroup }} />
                <RBHTextInput
                    prefix={<SearchOutlined style={{ color: Colors.grey600 }} translate={null} />}
                    placeholder='main.menu.modifieritem.search.placeholder'
                    id={'search-modifierItem-selection-input'}
                    style={{ width: '240px', borderColor: Colors.grey100, borderRadius: '8px' }}
                    value={searchText}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
                />
             </Row>
            <RBHTable2
                dataSource={filteredTableData}
                pagination={false}
                scroll={{ y: 400, x: 'auto' }}
                rowSelection={{
                    selectedRowKeys: tempSelectedIds,
                    onChange: (keys) => setTempSelectedIds(keys.map(Number)),
                    columnTitle: <></>,
                    getCheckboxProps: (record: any) => ({
                        disabled: isTempMaxReached && !tempSelectedIds.includes(record.id),
                    }),
                }}
                columns={[
                    { title: <FormattedMessage id="main.menu.modifieritem.column.name" />, dataIndex: 'menuModifierItemName', width: metrics.tableHeader.width180 },
                    { title: <FormattedMessage id="main.menu.modifieritem.column.nameEN" />, dataIndex: 'menuModifierItemNameEN', width: metrics.tableHeader.width180 },
                    { title: <FormattedMessage id="main.menu.modifieritem.column.price" />, dataIndex: 'menuModifierItemPrice', width: metrics.tableHeader.width130 },
                ]}
            />
            <Row justify="end" style={{ marginTop: 16, gap: 8 }}>
                <RBHButton
                    style={{
                        padding: '10px 24px',
                        border: '1px solid ' + Colors.grey200,
                        boxShadow: 'none',
                        borderRadius: 48,
                        color: Colors.grey600,
                        height: '100%',
                        ...textStyles.subtitle2,
                    }}
                    onClick={() => setIsItemModalOpen(false)}
                    text="main.button_cancel"
                />
                <RBHButton
                    style={{
                        padding: '10px 24px',
                        border: 0,
                        boxShadow: 'none',
                        borderRadius: 48,
                        background: Colors.violet600,
                        color: Colors.white,
                        height: '100%',
                        ...textStyles.subtitle2,
                    }}
                    onClick={handleConfirmItemModal}
                    text="main.button_confirm"
                />
            </Row>
        </Modal>
    )
}

export default SelectModifierItemModal;